"use client";
import React, { useState, useEffect } from "react";
import Link from "next/link";

const ToggleSection = ({ cityBikeLinks, cityCarLinks, cityMonthlyLinks }) => {
  // State for toggles
  const [showMoreBike, setShowMoreBike] = useState(false);
  const [showMoreCar, setShowMoreCar] = useState(false);
  const [showMoreMonthly, setShowMoreMonthly] = useState(false);

  // Display limits for responsiveness
  const [displayLimit, setDisplayLimit] = useState(23);

  useEffect(() => {
    const updateLimit = () => {
      setDisplayLimit(window.innerWidth <= 959 ? 5 : 23);
    };
    updateLimit();
    window.addEventListener("resize", updateLimit);
    return () => window.removeEventListener("resize", updateLimit);
  }, []);

  // Helper function to render sections.
  // The `links` object's KEY is already the full anchor text — e.g.
  // "Bike Rental in Agra" — returned by Seo::footerLinks{Bike,Car,Monthly}().
  // Don't prefix it again or you'll get "Bike rental in Bike Rental in Agra".
  const renderSection = (title, links, showMore, setShowMore) => {
    if (!links || Object.keys(links).length === 0) return null;

    // Accessibility: stable ID for the controlled links container
    const sectionId = title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

    // Filter out entries with null/empty URLs — defensive against bad data
    // from the API (a null url got rendered as href="null/" which Next.js Link
    // then resolved as a relative path, producing /<currentPath>/null/ 404s).
    const entries = Object.entries(links).filter(
      ([, url]) => typeof url === "string" && url.length > 0
    );

    if (entries.length === 0) return null;

    const shouldShowToggle = entries.length > displayLimit;

    return (
      <>
        <div className="w-full pt-6 pb-3 border-t border-lineDark first:border-t-0">
          <div className="text-sm text-white text-left max-md960:text-center my-[10px] flex-[0_0_100%]">{title}</div>
        </div>

        <div
          className="grid grid-cols-4 max-md960:grid-cols-1 gap-x-4 gap-y-[6px] max-md960:text-center pb-2 w-full"
          id={`${sectionId}-links`}
        >
          {/* All entries rendered to DOM so Google can crawl every city link.
              Items beyond displayLimit are visually hidden when showMore is false
              (CSS `display: none`), but they exist in the HTML source. This is
              the difference between SSR-visible links (good for SEO) and
              client-only-after-click links (orphans). */}
          {entries.map(([city, url], index) => {
            const beyondLimit = index >= displayLimit;
            const hidden = beyondLimit && !showMore;
            // Handle 3 shapes the API may return:
            //   1. fully-qualified URL  "https://sukuto.com/bike-rental-in-X"
            //   2. absolute path        "/bike-rental-in-X"
            //   3. bare slug            "bike-rental-in-X"
            // Only (3) needs a leading slash; otherwise next/link resolves it
            // as a relative path against the current page.
            let hrefWithSlash;
            if (/^https?:\/\//i.test(url)) {
              hrefWithSlash = url.endsWith("/") ? url : `${url}/`;
            } else {
              const absUrl = url.startsWith("/") ? url : `/${url}`;
              hrefWithSlash = absUrl.endsWith("/") ? absUrl : `${absUrl}/`;
            }
            return (
              <div key={index} className={hidden ? "hidden" : ""} aria-hidden={hidden ? "true" : undefined}>
                <Link
                  href={hrefWithSlash}
                  className="block text-white pb-[5px] text-left max-md960:text-center no-underline transition-colors duration-300 hover:text-[#ffdd57] hover:underline min-h-[28px]"
                  tabIndex={hidden ? -1 : undefined}
                >
                  {city}
                </Link>
              </div>
            );
          })}

          {/* Toggle button as the last grid item */}
          {shouldShowToggle && (
            <div className="whitespace-nowrap overflow-hidden text-ellipsis text-left max-md960:text-center max-md960:w-full">
              <button
                type="button"
                className="text-primary font-bold cursor-pointer transition-all duration-300 min-w-[44px] pb-[5px] border-none bg-transparent hover:underline hover:scale-105"
                onClick={() => setShowMore(!showMore)}
                aria-expanded={showMore}
                aria-controls={`${sectionId}-links`}
              >
                {showMore ? "Show Less" : "Show More"}
              </button>
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <>
      {renderSection("Our Bike Rental Services in Top Cities in India", cityBikeLinks, showMoreBike, setShowMoreBike)}
      {renderSection("Our Car Rental Services in Top Cities in India", cityCarLinks, showMoreCar, setShowMoreCar)}
      {renderSection("Our Monthly Rental Services in Top Cities in India", cityMonthlyLinks, showMoreMonthly, setShowMoreMonthly)}
    </>
  );
};

export default ToggleSection;
